import { useRef, useState, type DragEvent } from 'react'

interface Props {
  onFile: (file: File) => void
  disabled?: boolean
}

export default function UploadDropzone({ onFile, disabled }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const cameraRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragging(false)
    if (disabled) return
    const file = e.dataTransfer.files?.[0]
    if (file && file.type.startsWith('image/')) onFile(file)
  }

  function handleSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) onFile(file)
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault()
        setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      className={`w-full max-w-2xl border-2 border-dashed rounded-2xl p-12 text-center transition ${
        dragging ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/5' : 'border-white/15'
      } ${disabled ? 'opacity-50 pointer-events-none' : 'hover:border-white/30'}`}
    >
      <p className="text-lg font-medium mb-2">drop a photo</p>
      <p className="text-sm text-[var(--color-muted)] mb-6">
        jpg, png, webp · ideal: object centered, decent light
      </p>
      <div className="flex items-center justify-center gap-3">
        <button
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          className="px-6 py-3 bg-[var(--color-accent)] rounded-lg font-medium hover:opacity-90 transition glow-pulse"
        >
          choose file
        </button>
        <button
          onClick={() => cameraRef.current?.click()}
          disabled={disabled}
          className="px-6 py-3 border border-white/20 rounded-lg hover:bg-white/5 transition"
        >
          take photo
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleSelect}
        className="hidden"
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleSelect}
        className="hidden"
      />
    </div>
  )
}
